import React, { useEffect, useState } from "react";
import { styled } from "@stitches/react";
import { Button, CircularProgress } from "@material-ui/core";
import { useHistory, useParams } from "react-router";
import { Alert } from "@material-ui/lab";

import { PHONE_NUMBER } from "../../constants/company";

import { ContactDetails } from "../NewOrderView/ContactDetails";
import { DeliveryDetails } from "../NewOrderView/DeliveryDetails";
import { DeliveryTime } from "../NewOrderView/DeliveryTime";

import { NewOrderContext } from "../NewOrderView/NewOrderContext";
import api from "../../utils/api";
import { IOrderModel } from "../../models/OrderModel";

import { Container } from "semantic-ui-react";
import { Typography } from "@material-ui/core";

export const AdminCreateOrderView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const history = useHistory();

  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasServerError, setHasServerError] = useState(false);
  const [formDataState, setFormDataState] = useState<any>(null);

  const [isContactDetailsValid, setIsContactDetailsValid] = useState(false);
  const [isDeliveryDetailsValid, setIsDeliveryDetailsValid] = useState(false);
  const [isDeliveryTimeValid, setIsDeliveryTimeValid] = useState(false);

  useEffect(() => {
    const fetchLastOrder = async () => {
      try {
        setIsLoadingUserData(true);
        const res = await api.get(`/admin/create-order/${userId}`);
        const data = res.data;

        const flattenedData = {
          ...data.contactDetails,
          ...data.deliveryDetails,
          ...data.deliveryTime,
        };

        setFormDataState(flattenedData);
        setHasServerError(false);

        // Validate initially loaded data
        setIsContactDetailsValid(validateContactDetails(flattenedData));
        setIsDeliveryDetailsValid(validateDeliveryDetails(flattenedData));
        setIsDeliveryTimeValid(validateDeliveryTime(flattenedData));
      } catch (e) {
        console.error(e);
        setHasServerError(true);
      } finally {
        setIsLoadingUserData(false);
      }
    };
    fetchLastOrder();
  }, [userId]);

  const validateContactDetails = (data: any) => {
    return Boolean(
      data.name?.trim() &&
      /^\d{10}$/.test(data.phoneNumber || '') &&
      data.address?.trim() &&
      data.city?.trim() &&
      data.zipCode?.trim()
    );
  };

  const validateDeliveryDetails = (data: any) => {
    return Boolean(
      data.isContactPersonRequired !== undefined &&
      data.isGpsAccurate !== undefined &&
      data.isGatedProperty !== undefined &&
      data.tankConnectionDetails?.trim()?.length > 0 &&
      data.tankDistance?.trim()?.length > 0 &&
      data.service?.trim()
    );
  };

  const validateDeliveryTime = (data: any) => {
    return Boolean(
      typeof data.startTime === "string" &&
      data.startTime.trim().length > 0 &&
      typeof data.driverId === "string" &&
      data.driverId.trim().length > 0
    );
  };

  const submitNewOrder = async (): Promise<IOrderModel> => {
    if (!formDataState) throw new Error("Form data not loaded");

    setIsSubmitting(true);
    try {
      const payload = { ...formDataState };
      const res = await api.post(`/admin/create-order/${userId}`, payload);
      history.push("/admin/orders?success=true");
      return res.data;
    } catch (e) {
      setHasServerError(true);
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingUserData) {
    return (
      <StyledCircularProgressContainer>
        <CircularProgress />
      </StyledCircularProgressContainer>
    );
  }

  return (
    <StyledContainer text style={{ marginTop: "3rem", padding: "0" }}>
      <Typography variant="h3">
        Create Order for {formDataState?.name || ""}
      </Typography>
      <Typography variant="body1" style={{ margin: "1rem 0" }}>
        Create a new order for a customer base from last order.
      </Typography>

      <NewOrderContext.Provider
        value={{
          formData: formDataState,
          setContactDetails: (fields) => {
            setFormDataState((prev: any) => {
              const updated = { ...prev, ...fields };
              setIsContactDetailsValid(validateContactDetails(updated));
              return updated;
            });
          },
          setDeliveryDetails: (fields) => {
            setFormDataState((prev: any) => {
              const updated = { ...prev, ...fields };
              setIsDeliveryDetailsValid(validateDeliveryDetails(updated));
              return updated;
            });
          },
          setDeliveryTime: (fields) => {
            setFormDataState((prev: any) => {
              const updated = { ...prev, ...fields };
              setIsDeliveryTimeValid(validateDeliveryTime(updated));
              return updated;
            });
          },
          isContactDetailsValid,
          isDeliveryDetailsValid,
          isDeliveryTimeValid,
          isLoadingUserData,
          submitNewOrder,
        }}
      >
        <ContactDetails />
        <DeliveryDetails />
        <DeliveryTime />

        <StyledSubmitButtonWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={submitNewOrder}
            disabled={
              isSubmitting ||
              !isContactDetailsValid ||
              !isDeliveryDetailsValid ||
              !isDeliveryTimeValid
            }
          >
            Submit
          </Button>

          {hasServerError && (
            <StyledServerAlert severity="error">
              Uh oh! Our computers screwed up again. Please try again later or
              call us at {PHONE_NUMBER}.
            </StyledServerAlert>
          )}
        </StyledSubmitButtonWrapper>
      </NewOrderContext.Provider>
    </StyledContainer>
  );
};

const StyledSubmitButtonWrapper = styled("div", {
  marginTop: "2rem",
});

const StyledServerAlert = styled(Alert, {
  marginTop: "2rem",
});

const StyledCircularProgressContainer = styled("div", {
  width: "100%",
  display: "flex",
  justifyContent: "center",
});

const StyledContainer = styled(Container, {
  margin: "2rem 0",
});

